import axios, { type InternalAxiosRequestConfig } from "axios";

export interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  requestId?: string;
  timestamp: string;
}

export const http = axios.create({
  baseURL: "/api/v1",
  timeout: 15_000,
  withCredentials: true,
});

interface RetryRequestConfig extends InternalAxiosRequestConfig {
  _retriedAfterRefresh?: boolean;
  _retriedAfterCsrf?: boolean;
  _csrfToken?: string;
}

let refreshPromise: Promise<void> | null = null;
let csrfPromise: Promise<string> | null = null;

http.interceptors.request.use(async (config: RetryRequestConfig) => {
  let csrfToken = config._csrfToken || readCookie("csrf_token");
  if (!csrfToken && needsCsrf(config)) {
    try {
      csrfToken = await sessionCsrfToken();
    } catch (error) {
      // A session refresh must retry the original write, not the token lookup.
      if (axios.isAxiosError(error)) error.config = config;
      throw error;
    }
  }
  if (csrfToken) config.headers["X-CSRF-Token"] = csrfToken;
  return config;
});

http.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const config = error?.config as RetryRequestConfig | undefined;
    const url = String(config?.url ?? "");
    if (
      error?.response?.status === 403 &&
      error?.response?.data?.error?.message === "CSRF 校验失败" &&
      config && needsCsrf(config) && !config._retriedAfterCsrf
    ) {
      config._retriedAfterCsrf = true;
      config._csrfToken = await sessionCsrfToken();
      return http.request(config);
    }
    if (
      error?.response?.status === 401 &&
      config &&
      !config._retriedAfterRefresh &&
      !url.includes("/auth/login") &&
      !url.includes("/auth/dingtalk") &&
      !url.includes("/auth/refresh")
    ) {
      config._retriedAfterRefresh = true;
      try {
        refreshPromise ??= axios
          .post("/api/v1/auth/refresh", {}, { withCredentials: true })
          .then(() => undefined)
          .finally(() => {
            refreshPromise = null;
          });
        await refreshPromise;
        delete config._csrfToken;
        return http.request(config);
      } catch {
        clearLegacyTokens();
        if (window.location.pathname !== "/login")
          window.location.assign("/login");
      }
    }
    return Promise.reject(error);
  },
);

function needsCsrf(config: RetryRequestConfig) {
  if (["get", "head", "options"].includes(String(config.method).toLowerCase())) return false;
  const path = String(config.url ?? "").split("?")[0];
  return !["/auth/login", "/auth/refresh", "/auth/dingtalk/inside"].includes(path ?? "");
}

function sessionCsrfToken() {
  csrfPromise ??= axios
    .get<ApiEnvelope<{ csrfToken: string }>>("/api/v1/auth/csrf", { withCredentials: true, timeout: 15_000 })
    .then(({ data }) => {
      if (!data.data?.csrfToken) throw new Error("无法获取会话校验信息，请重新登录");
      return data.data.csrfToken;
    })
    .finally(() => { csrfPromise = null; });
  return csrfPromise;
}

function readCookie(name: string) {
  const prefix = `${encodeURIComponent(name)}=`;
  const item = document.cookie
    .split(";")
    .map((value) => value.trim())
    .find((value) => value.startsWith(prefix));
  return item ? decodeURIComponent(item.slice(prefix.length)) : undefined;
}

function clearLegacyTokens() {
  window.localStorage.removeItem("pms_access_token");
  window.localStorage.removeItem("pms_refresh_token");
}
